import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { ClubPage } from "./club.$slug";
import {
  clubGallery,
  getClubBySlug,
  getClubReviews,
  relatedClubs,
} from "../lib/api/lidoo.functions";
import { DICTS, fill, LOCALIZED_LANGS, regionName, type Lang } from "../lib/i18n";
import { isIndexableLocale, NOINDEX_META } from "../lib/indexing";
import { townName } from "../lib/place";
import { catalogueHreflang } from "../lib/seo";
import { clubTitle } from "../lib/titles";

export const Route = createFileRoute("/$locale/club/$slug")({
  beforeLoad: ({ params }) => {
    if (params.locale === "en") {
      throw redirect({ to: "/club/$slug", params: { slug: params.slug } });
    }
    if (!LOCALIZED_LANGS.includes(params.locale as Lang)) throw notFound();
  },
  loader: async ({ params }) => {
    const [{ club, socials, translated }, { reviews }, { photos }, related] = await Promise.all([
      getClubBySlug({ data: { slug: params.slug, lang: params.locale } }),
      getClubReviews({ data: { clubSlug: params.slug } }),
      clubGallery({ data: { slug: params.slug } }),
      relatedClubs({ data: { slug: params.slug } }),
    ]);
    if (!club) throw notFound();
    return { club, socials, reviews, photos, related: related.clubs, translated };
  },
  head: (ctx) => {
    const club = ctx.loaderData?.club;
    // The stored town can read "Pachino, near Marzamemi", and that English
    // "near" was going straight into the <title> and the meta description of
    // every non-English club page. Those two strings are what a Dutch or
    // Italian searcher sees in Google before they see anything else.
    const d = DICTS[ctx.params.locale as Lang] ?? DICTS.en;
    const tr = (k: string) => d[k] ?? DICTS.en[k] ?? k;
    const town = club ? townName(club.town, tr) : "";
    const region = club ? regionName(club.region_key, club.region_label, tr) : "";
    // Three reasons to keep this page out of the index, any one is enough:
    // the language is machine-translated and not on the indexable list
    // (lib/indexing.ts), the club has no original write-up (same gate as the
    // English route), or the write-up in this language is missing and the
    // page fell back to the English text, which makes it a copy of /club/x.
    const noindex =
      !isIndexableLocale(ctx.params.locale) ||
      !club?.long_description ||
      ctx.loaderData?.translated === false;
    return {
      meta: [
        {
          title: club
            ? clubTitle({
                name: club.name,
                town,
                typeWord: tr("club_title_type"),
                typeInTown: fill(tr("club_title_in"), { town }),
              })
            : "Beach club | sunbedly",
        },
        {
          name: "description",
          content: club ? `${club.name}, ${town}, ${region}.` : "",
        },
        ...(club && noindex ? [NOINDEX_META] : []),
      ],
      links: catalogueHreflang(`/club/${ctx.params.slug}`, ctx.params.locale),
    };
  },
  component: () => <ClubPage {...Route.useLoaderData()} />,
});
